import type {
  AdminFacilityProcess,
  AdminFacilityProcessesResponse,
  CreateAdminFacilityProcessBody,
  UpdateAdminFacilityProcessBody,
} from '@/api/(admin)/facility-processes/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * All three are the ported `/api/admin/facility-processes` routes, on
 * `API_ROOT_URL`.
 *
 * Named `…Admin…` to keep them apart from `fetchFacilityProcessOptions` in
 * `@/api/dropdowns`, which reads the customer-facing list.
 */

/** The list. Takes a pre-built query string; paging, sort and filter live in the hook. */
export async function fetchAdminFacilityProcesses(
  qs: string,
): Promise<AdminFacilityProcessesResponse> {
  return apiFetch<AdminFacilityProcessesResponse>(
    `/api/admin/facility-processes?${qs}`,
    {},
    API_ROOT_URL,
  );
}

/** Creates one. A POST to the collection; answers `{ data, metadata }`. */
export async function createAdminFacilityProcess(
  body: CreateAdminFacilityProcessBody,
): Promise<{ data: AdminFacilityProcess }> {
  return apiFetch<{ data: AdminFacilityProcess }>(
    '/api/admin/facility-processes',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * Updates one; answers `{ data, metadata }`.
 *
 * A PUT, not a PATCH: the route declares `PUT /:id` and marks both `name` and
 * `productionProcessId` required, so the caller always sends the full pair.
 */
export async function updateAdminFacilityProcess(
  id: string,
  body: UpdateAdminFacilityProcessBody,
): Promise<{ data: AdminFacilityProcess }> {
  return apiFetch<{ data: AdminFacilityProcess }>(
    `/api/admin/facility-processes/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}
